import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/auth.context';
import { UserActionTypes, useUser } from '../context/user.context';
import { checkIdp } from '../helpers/check-idp';
import { DEFAULT_IDP } from '../helpers/constants';
import { usePublicComputerGate } from '../hooks/use-public-computer-gate';
import LoginForm from './login-form.component';
import LoginLoader from './login-loader.component';
import LoginSchema, { useLoginSchema } from './login-schema.component';

export interface LoginResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  selectedSchema: string;
}

export interface LoginProps {
  onLoginSuccess?: (result: LoginResult) => void;
  redirectPath?: string;
  loginQueryParams?: unknown;
  username?: string;
}

export default function Login({ onLoginSuccess, redirectPath, loginQueryParams, username }: LoginProps) {
  const { state, dispatch } = useUser();
  const { signinRedirect } = useAuth();
  const { onSchemaSelected } = useLoginSchema(redirectPath, onLoginSuccess);
  const { t } = useTranslation();

  // Clear Cognito's surviving session before authorising if the last session was public. While
  // it does, isGating is true and every redirect below must stand down, or a signinRedirect would
  // race the logout back onto Cognito's replay. See the hook.
  const isGating = usePublicComputerGate(state.auth);

  // biome-ignore lint/correctness/useExhaustiveDependencies: isAuthenticated and setIdp are intentionally omitted to prevent re-running on auth state changes. Keyed on username plus isGating, so a failed gate-logout that releases the gate resumes this auto-redirect instead of leaving it stuck on the loader
  useEffect(() => {
    if (username && !isAuthenticated && !isGating) {
      const autoRedirect = async () => {
        const idp = await checkIdp(username);
        setIdp(idp);
      };

      // Workaround to ensure component is rendered before redirect happens
      setTimeout(autoRedirect);
    }
  }, [username, isGating]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: signinRedirect and loginQueryParams are intentionally omitted as they are stable from the Auth context and props, and including them could cause redirect loops. isGating is included so a form submitted while the gate's logout is pending redirects once the gate releases (a failed gate-logout)
  useEffect(() => {
    if (state.idp && state.loginStatus === 'idp' && !isGating) {
      signinRedirect(state.idp, loginQueryParams);
    }
  }, [state.idp, state.loginStatus, isGating]);

  const isAuthenticated = state.auth;
  const tokens = state.tokens;

  const setIdp = (idp: string) => {
    dispatch({
      type: UserActionTypes.UpdateUser,
      payload: {
        auth: false,
        idp,
        isDefaultIdp: idp === DEFAULT_IDP,
        loginStatus: 'idp',
      },
    });
  };

  return (
    <>
      {/* While gating, a logout redirect is in flight — show a loader, not the form, so the
          user cannot type an email only to be navigated away. */}
      {!isAuthenticated && !username && !isGating && <LoginForm setIdp={setIdp} />}
      {!isAuthenticated && !username && isGating && <LoginLoader title={t(($) => $.redirectingToLogin)} />}
      {isAuthenticated && tokens && <LoginSchema idToken={tokens.id_token} onSchemaSelected={onSchemaSelected} />}
      {!isAuthenticated && username && <LoginLoader title={t(($) => $.redirectingToLogin)} />}
    </>
  );
}
